import React from 'react';
import { SectionContext } from '../Section/Sections';
import HeaderManagement from './HeaderManagementList';
import { Button, Grid } from '@mui/material';
import { useState, useEffect } from 'react';
import { SectionServices } from '../Section/SectionServices';
import Swal from 'sweetalert2';
import Loading from '../../../../components/Loader/Loading';
import PageHeaderLayout from '../../../../components/Layout/PageHeaderLayout';
import { Add, ListAlt, PhoneAndroid } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MobilePreview from '../Section/MobilePreview';

const HeaderManagementWrapper = () => {
    const navigate = useNavigate();
    const isFooterManagement = window.location.pathname.includes('/footer-management');
    const pageType = isFooterManagement ? 'footer' : 'header';
    const pageTitle = isFooterManagement ? 'Footer Management' : 'Header Management';
    const createLabel = isFooterManagement ? 'Create Footer' : 'Create Header';
    const [previewOpen, setPreviewOpen] = useState(false);
    const [allRowCount, setAllRowCount] = useState(0);
    const [list, setList] = useState([]);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
        pageType,

        filters: {
            searchKey: "",
            sortBy: 'position',
            sortType: 'ASC',
            filterOptions: []
        }
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setPaginationModel(prev => ({
            ...prev,
            page: 0,
            pageType
        }));
    }, [pageType]);

    const fetchSectionList = async () => {
        try {
            setLoading(true);
            const listResponse = await SectionServices.sectionList({
                page: paginationModel.page,
                limit: paginationModel.pageSize,
                pageType,

                filters: {
                    searchKey: paginationModel.filters.searchKey,
                    sortBy: paginationModel.filters.sortBy,
                    sortType: paginationModel.filters.sortType,
                    filterOptions: paginationModel.filters.filterOptions || []
                }
            });
            if (listResponse.success == '1') {
                setList(listResponse.data.list);
                setAllRowCount(listResponse.data.totalItems);
            }
        } catch (error) {
            Swal.fire(error.message, '', 'error');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchSectionList();
    }, [paginationModel, pageType]);

    const handleStatusChange = async (data, sectionId) => {
        const result = await Swal.fire({
            text: "Do you want to Change Section Status now?",
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Change now!',
            cancelButtonText: 'No, Cancel',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showCloseButton: false,
        });

        if (result.isConfirmed) {
            try {
                Swal.fire({
                    title: 'Processing...',
                    text: 'Please wait while we update the section status',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                const statusChangeResponse = await SectionServices.toggleStatus(sectionId);

                if (statusChangeResponse.success == '1') {
                    await fetchSectionList();
                    await Swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: statusChangeResponse.message || 'Section Status updated successfully',
                        timer: 1500
                    });
                    return true;
                } else {
                    await Swal.fire({
                        icon: 'error',
                        title: 'Error!',
                        text: statusChangeResponse.message || 'Failed to update section status'
                    });
                    return false;
                }
            } catch (error) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: 'An error occurred while updating the section status'
                });
                return false;
            }
        }
        return false;
    };

    const handleDeleteSection = async (sectionId) => {
        const result = await Swal.fire({
            text: "Are you sure you want to delete this section?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, Delete!',
            cancelButtonText: 'Cancel',
            allowOutsideClick: false,
            allowEscapeKey: false,
        });

        if (result.isConfirmed) {
            try {
                Swal.fire({
                    title: 'Processing...',
                    text: 'Please wait while we delete the section',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                const deleteResponse = await SectionServices.deleteSection(sectionId);

                if (deleteResponse.success == '1') {
                    await fetchSectionList();
                    await Swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: deleteResponse.message || 'Section deleted successfully',
                        timer: 1500
                    });
                    return true;
                } else {
                    await Swal.fire({
                        icon: 'error',
                        title: 'Error!',
                        text: deleteResponse.message || 'Failed to delete section'
                    });
                    return false;
                }
            } catch (error) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: 'An error occurred while deleting the section'
                });
                return false;
            }
        }
        return false;
    };

    const handlePaginationChange = (newPagination) => {
        setPaginationModel({
            ...paginationModel,
            page: newPagination.page,
            pageSize: newPagination.pageSize
        });
    };

    const [sortModel, setSortModel] = useState([
        {
            field: paginationModel.filters.sortBy,
            sort: paginationModel.filters.sortType?.toLowerCase(),
        },
    ]);

    const handleSortingChange = (newSortModel) => {
        if (newSortModel && newSortModel.length > 0) {
            setSortModel(newSortModel);
            setPaginationModel(prev => ({
                ...prev,
                filters: {
                    ...prev.filters,
                    sortBy: newSortModel[0].field,
                    sortType: newSortModel[0].sort?.toUpperCase()
                }
            }));
        }
    };

    const handleFilterChange = (newFilters) => {
        setPaginationModel(prev => ({
            ...prev,
            ...newFilters
        }));
    };

    return (
        <SectionContext.Provider value={{
            list,
            setList,
            fetchSectionList,
            loading,
            setLoading,
            allRowCount,
            paginationModel,
            handlePaginationChange,
            handleSortingChange,
            sortModel,
            handleStatusChange,
            handleDeleteSection,
            handleFilterChange
        }}>
            {loading && <Loading />}
            <PageHeaderLayout headerTitle={pageTitle}>
                <Button
                    sx={{ textTransform: 'none' }}
                    size="small"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate(`/customer-app/home-screen/section/0?pageType=${pageType}`)}
                >
                    {createLabel}
                </Button>
                <Button
                    sx={{ textTransform: 'none' }}
                    size="small"
                    variant="outlined"
                    startIcon={<ListAlt />}
                    onClick={() => navigate(`/customer-app/home-screen/section-items?pageType=${pageType}`)}
                >
                    {isFooterManagement ? 'Footer Items' : 'Header Items'}
                </Button>
                <Button
                    sx={{ textTransform: 'none' }}
                    size="small"
                    variant="outlined"
                    startIcon={<PhoneAndroid />}
                    onClick={() => setPreviewOpen(true)}
                >
                    Show Preview
                </Button>
            </PageHeaderLayout>
            <Grid container spacing={2}>
                <Grid item xs={12} lg={12}>
                    <HeaderManagement />
                </Grid>
                {/* <Grid item xs={12} lg={3}>
                    <SectionReorder />
                </Grid> */}
            </Grid>
            <MobilePreview
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                formData={null}
                sectionId={null}
            />
        </SectionContext.Provider>
    );
};

export default HeaderManagementWrapper;
